import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Home, ClipboardList, AlertTriangle, ArrowRight, Plus } from "lucide-react";

const STAT = [
  { key: "properties", label: "Properties", icon: Building2, color: "text-blue-600 bg-blue-50" },
  { key: "units", label: "Units", icon: Home, color: "text-indigo-600 bg-indigo-50" },
  { key: "inspections", label: "Inspections", icon: ClipboardList, color: "text-emerald-600 bg-emerald-50" },
  { key: "major_findings", label: "Major Findings", icon: AlertTriangle, color: "text-red-600 bg-red-50" },
];

export default function Dashboard() {
  const { user, org } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/inspections").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">Welcome back, {user?.name}. {org?.subscription_status !== "active" && "You're on a trial."}</p>
        </div>
        {user?.role !== "viewer" && (
          <Link to="/app/properties" data-testid="dashboard-new-inspection-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow hover:bg-blue-700">
            <Plus className="h-4 w-4" /> New Inspection
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {STAT.map((s) => (
          <div key={s.key} data-testid={`stat-${s.key}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.color}`}><s.icon className="h-5 w-5" /></div>
            <div className="mt-3 font-display text-3xl font-extrabold text-slate-900">{stats ? stats[s.key] : "–"}</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-900">Recent Inspections</h2>
          <Link to="/app/inspections" className="text-sm font-semibold text-blue-600">View all</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {recent.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">No inspections yet. Start one from a property.</div>}
          {recent.map((i) => (
            <button key={i.id} data-testid={`recent-inspection-${i.id}`} onClick={() => nav(`/app/inspections/${i.id}`)}
              className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-slate-50">
              <div>
                <div className="font-semibold text-slate-900">{i.property_name} · Unit {i.unit_number}</div>
                <div className="text-xs text-slate-500">{i.inspection_date} · {i.finding_count} findings · {i.status.replace("_", " ")}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
